import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Analytics {
  totalUsers: number;
  activeUsers: number;
  totalContent: number;
  totalRevenue: number;
  contentByType: { type: string; count: number }[];
  usersByPlan: { plan: string; count: number }[];
  recentActivity: { date: string; users: number; content: number }[];
}

export const useAdminAnalytics = () => {
  const [analytics, setAnalytics] = useState<Analytics>({
    totalUsers: 0,
    activeUsers: 0,
    totalContent: 0,
    totalRevenue: 0,
    contentByType: [],
    usersByPlan: [],
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      // Fetch total users
      const { count: totalUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Fetch active users (logged in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const { count: activeUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('last_active', thirtyDaysAgo.toISOString());

      // Fetch total content
      const { count: totalContent } = await supabase
        .from('generated_content')
        .select('*', { count: 'exact', head: true });

      // Fetch total revenue (sum of all credit purchases)
      const { data: purchases } = await supabase
        .from('credit_purchases')
        .select('amount_paid')
        .eq('status', 'completed');

      const totalRevenue = purchases?.reduce((sum, p) => sum + Number(p.amount_paid), 0) || 0;

      // Fetch content by type
      const { data: contentByType } = await supabase
        .from('generated_content')
        .select('type')
        .order('type');

      const contentTypeCounts = contentByType?.reduce((acc: any, item) => {
        acc[item.type] = (acc[item.type] || 0) + 1;
        return acc;
      }, {});

      // Fetch users by plan
      const { data: usersByPlan } = await supabase
        .from('profiles')
        .select('plan')
        .order('plan');

      const planCounts = usersByPlan?.reduce((acc: any, item) => {
        const plan = item.plan || 'free';
        acc[plan] = (acc[plan] || 0) + 1;
        return acc;
      }, {});

      setAnalytics({
        totalUsers: totalUsers || 0,
        activeUsers: activeUsers || 0,
        totalContent: totalContent || 0,
        totalRevenue,
        contentByType: Object.entries(contentTypeCounts || {}).map(([type, count]) => ({
          type,
          count: count as number
        })),
        usersByPlan: Object.entries(planCounts || {}).map(([plan, count]) => ({
          plan,
          count: count as number
        })),
        recentActivity: []
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch analytics',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  return {
    analytics,
    loading,
    fetchAnalytics
  };
};
